import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Box, Typography, Paper, CircularProgress,
  Grid, Card, CardContent, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Avatar, Button, LinearProgress,
  Alert, TextField, FormControl,
  RadioGroup, FormControlLabel, Radio, FormLabel, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowBack as BackIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Schedule as LateIcon,
  School as SchoolIcon,
  AccountBalance as FeeIcon,
  Grade as MarksIcon,
  ChildCare as DaycareIcon,
  Today as UpdatesIcon,
  AssignmentLate as CareNoteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  NoteAdd as NoteAddIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { parentAPI } from '../../services/api';
import ResponsiveDataView from '@components/mobile/ResponsiveDataView';
import MobileFormDialog from '@components/mobile/MobileFormDialog';

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const statusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'present': return 'success';
    case 'absent': return 'error';
    case 'late': return 'warning';
    case 'excused': return 'info';
    default: return 'default';
  }
};

const StatusIcon = ({ status }) => {
  switch (status?.toLowerCase()) {
    case 'present': return <PresentIcon fontSize="small" color="success" />;
    case 'absent': return <AbsentIcon fontSize="small" color="error" />;
    case 'late': return <LateIcon fontSize="small" color="warning" />;
    default: return null;
  }
};

function AttendanceTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    parentAPI.getChildAttendance(studentId)
      .then(res => { setData(res.data); setError(''); })
      .catch(() => setError('Failed to load attendance.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  const { stats, records } = data;

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Present', value: stats.presentDays, color: 'success' },
          { label: 'Absent', value: stats.absentDays, color: 'error' },
          { label: 'Late', value: stats.lateDays, color: 'warning' },
          { label: 'Attendance %', value: `${stats.percentage}%`, color: stats.percentage >= 75 ? 'success' : 'error' }
        ].map(s => (
          <Grid item xs={6} sm={3} key={s.label}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h5" fontWeight={700} color={`${s.color}.main`}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {stats.totalDays > 0 && (
        <Box mb={3}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="body2" color="text.secondary">Overall Attendance</Typography>
            <Typography variant="body2" fontWeight={600}>{stats.percentage}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={stats.percentage}
            color={stats.percentage >= 75 ? 'success' : stats.percentage >= 60 ? 'warning' : 'error'}
            sx={{ height: 8, borderRadius: 4 }}
          />
          {stats.percentage < 75 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Attendance below 75%. Regular attendance is important for academic progress.
            </Alert>
          )}
        </Box>
      )}

      {records.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No attendance records found.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Remarks</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell>{formatDate(r.date)}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <StatusIcon status={r.status} />
                      <Chip
                        label={r.status || 'Unknown'}
                        color={statusColor(r.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{r.remarks || '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function MarksTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    parentAPI.getChildMarks(studentId)
      .then(res => { setData(res.data); setError(''); })
      .catch(() => setError('Failed to load marks.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  const marks = data?.marks || [];

  if (marks.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
        <MarksIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No marks recorded yet.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {marks.map((examGroup) => (
        <Paper key={examGroup.exam?.id} elevation={1} sx={{ mb: 3 }}>
          <Box
            sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: '12px 12px 0 0',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>{examGroup.exam?.name}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Academic Year: {examGroup.exam?.academic_year}
              </Typography>
            </Box>
            <Chip
              label={`${examGroup.overallPercentage}% overall`}
              sx={{
                bgcolor: examGroup.overallPercentage >= 75 ? '#10b981' :
                         examGroup.overallPercentage >= 60 ? '#f59e0b' : '#ef4444',
                color: 'white', fontWeight: 700
              }}
            />
          </Box>
          <ResponsiveDataView
            rows={examGroup.subjects}
            keyExtractor={(s, idx) => idx}
            renderCard={(s, idx) => (
              <Card key={idx} variant="outlined" sx={{ mb: 1.5, mx: 2, mt: idx === 0 ? 2 : 0 }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.subject?.subject_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.subject?.subject_code}</Typography>
                    </Box>
                    <Chip
                      label={`${s.percentage}%`}
                      size="small"
                      color={s.percentage >= 75 ? 'success' : s.percentage >= 60 ? 'warning' : 'error'}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {s.marks ?? '—'}/{s.maxMarks ?? '—'} marks
                  </Typography>
                  {s.remarks && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Remarks: {s.remarks}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            )}
            footer={
              <Box display="flex" justifyContent="space-between" px={2} py={1.5} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight={700}>Total</Typography>
                <Typography fontWeight={700}>
                  {examGroup.totalMarks}/{examGroup.totalMax} ({examGroup.overallPercentage}%)
                </Typography>
              </Box>
            }
            table={
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell align="center"><strong>Marks</strong></TableCell>
                      <TableCell align="center"><strong>Max</strong></TableCell>
                      <TableCell align="center"><strong>%</strong></TableCell>
                      <TableCell><strong>Remarks</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {examGroup.subjects.map((s, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {s.subject?.subject_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {s.subject?.subject_code}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography fontWeight={600}>{s.marks ?? '—'}</Typography>
                        </TableCell>
                        <TableCell align="center">{s.maxMarks ?? '—'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={`${s.percentage}%`}
                            size="small"
                            color={s.percentage >= 75 ? 'success' : s.percentage >= 60 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">{s.remarks || '—'}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="center"><strong>{examGroup.totalMarks}</strong></TableCell>
                      <TableCell align="center"><strong>{examGroup.totalMax}</strong></TableCell>
                      <TableCell align="center">
                        <strong>{examGroup.overallPercentage}%</strong>
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        </Paper>
      ))}
    </Box>
  );
}

function FeesTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    parentAPI.getChildFees(studentId)
      .then(res => { setData(res.data); setError(''); })
      .catch(() => setError('Failed to load fee information.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        {[
          { label: 'Total Fees', value: fmt(data?.totalFees), color: 'primary' },
          { label: 'Paid', value: fmt(data?.totalPaid), color: 'success' },
          { label: 'Pending', value: fmt(data?.totalPending), color: data?.totalPending > 0 ? 'error' : 'success' }
        ].map(s => (
          <Grid item xs={12} sm={4} key={s.label}>
            <Card elevation={1}>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} color={`${s.color}.main`}>{s.value}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {data?.totalPending > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You have pending fees of {fmt(data.totalPending)}. Please contact the school office to make payment.
        </Alert>
      )}

      {(!data?.feeDetails || data.feeDetails.length === 0) ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <FeeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No fee records found.</Typography>
        </Paper>
      ) : (
        <ResponsiveDataView
          rows={data.feeDetails}
          keyExtractor={(fee) => fee.feeId}
          renderCard={(fee) => (
            <Card variant="outlined" sx={{ mb: 1.5 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="body2" fontWeight={600}>{fee.feeType}</Typography>
                  <Chip
                    label={fee.status}
                    size="small"
                    color={fee.status === 'Paid' ? 'success' : fee.status === 'Partial' ? 'warning' : 'error'}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">Amount / Paid</Typography>
                  <Typography variant="body2">{fmt(fee.amount)} / {fmt(fee.paid)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" color="text.secondary">Remaining</Typography>
                  <Typography color={fee.remaining > 0 ? 'error.main' : 'success.main'} fontWeight={600}>
                    {fmt(fee.remaining)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Due: {formatDate(fee.dueDate)}
                </Typography>
              </CardContent>
            </Card>
          )}
          table={
            <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Fee Type</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell align="right"><strong>Paid</strong></TableCell>
                    <TableCell align="right"><strong>Remaining</strong></TableCell>
                    <TableCell><strong>Due Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.feeDetails.map((fee) => (
                    <TableRow key={fee.feeId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{fee.feeType}</Typography>
                      </TableCell>
                      <TableCell align="right">{fmt(fee.amount)}</TableCell>
                      <TableCell align="right">{fmt(fee.paid)}</TableCell>
                      <TableCell align="right">
                        <Typography color={fee.remaining > 0 ? 'error.main' : 'success.main'} fontWeight={600}>
                          {fmt(fee.remaining)}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDate(fee.dueDate)}</TableCell>
                      <TableCell>
                        <Chip
                          label={fee.status}
                          size="small"
                          color={fee.status === 'Paid' ? 'success' : fee.status === 'Partial' ? 'warning' : 'error'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          }
        />
      )}

      {data?.payments && data.payments.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} mt={4} mb={2}>Payment History</Typography>
          <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell align="right"><strong>Amount Paid</strong></TableCell>
                  <TableCell><strong>Method</strong></TableCell>
                  <TableCell><strong>Transaction ID</strong></TableCell>
                  <TableCell><strong>Notes</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.payments.map(p => (
                  <TableRow key={p.id} hover>
                    <TableCell>{formatDate(p.payment_date)}</TableCell>
                    <TableCell align="right">
                      <Typography color="success.main" fontWeight={600}>{fmt(p.amount_paid)}</Typography>
                    </TableCell>
                    <TableCell>{p.payment_method || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {p.transaction_id || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{p.notes || '—'}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}

const fmtDaycareTime = (ts) => ts ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';
const fmtMoney = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const STATUS_CHIP = { open: 'warning', completed: 'success', void: 'default' };
const MOOD_COLORS = { Happy: 'success', Calm: 'info', Energetic: 'primary', Tired: 'warning', Upset: 'error', Sick: 'error' };

function DaycareTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => {
    setLoading(true);
    parentAPI.daycare.getChild(studentId, { year, month })
      .then(res => { setData(res.data); setError(''); })
      .catch(() => setError('Failed to load daycare data.'))
      .finally(() => setLoading(false));
  }, [studentId, year, month]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  const sessions = data?.sessions || [];
  const monthDue = data?.month_due || 0;
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <Box>
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6}>
          <Card elevation={1}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} color="primary.main">{sessions.length}</Typography>
              <Typography variant="caption" color="text.secondary">Sessions this month</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Card elevation={1}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h5" fontWeight={700} color={monthDue > 0 ? 'error.main' : 'success.main'}>
                {fmtMoney(monthDue)}
              </Typography>
              <Typography variant="caption" color="text.secondary">Due for {monthName}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {sessions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DaycareIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No daycare sessions for {monthName}.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Check In</strong></TableCell>
                <TableCell><strong>Check Out</strong></TableCell>
                <TableCell><strong>Hours</strong></TableCell>
                <TableCell align="right"><strong>Amount</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sessions.map(s => (
                <TableRow key={s.id} hover>
                  <TableCell>{fmtDaycareTime(s.check_in)}</TableCell>
                  <TableCell>{s.check_out ? fmtDaycareTime(s.check_out) : <em style={{ color: '#888' }}>In progress</em>}</TableCell>
                  <TableCell>{s.billed_hours != null ? `${s.billed_hours}h` : '—'}</TableCell>
                  <TableCell align="right">
                    {s.amount != null ? <Typography fontWeight={600}>{fmtMoney(s.amount)}</Typography> : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip label={s.status} color={STATUS_CHIP[s.status] || 'default'} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

function DailyUpdatesTab({ studentId }) {
  const [loading, setLoading] = useState(true);
  const [updates, setUpdates] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    parentAPI.dailyUpdates.forStudent(studentId)
      .then(res => { setUpdates(res.data.updates || []); setError(''); })
      .catch(() => setError('Failed to load daily updates.'))
      .finally(() => setLoading(false));
  }, [studentId]);

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;

  if (updates.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
        <UpdatesIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No daily updates yet.</Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {updates.map(u => (
        <Paper key={u.id} elevation={1} sx={{ p: 2.5, mb: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              {new Date(u.update_date).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
            </Typography>
            {u.mood && (
              <Chip label={u.mood} size="small" color={MOOD_COLORS[u.mood] || 'default'} />
            )}
          </Box>
          <Typography variant="body2" mb={1}>{u.content}</Typography>
          {(u.meals || u.nap || u.activities) && (
            <Box display="flex" gap={2} flexWrap="wrap" mt={1}>
              {u.meals      && <Typography variant="caption" color="text.secondary">🍽 {u.meals}</Typography>}
              {u.nap        && <Typography variant="caption" color="text.secondary">😴 {u.nap}</Typography>}
              {u.activities && <Typography variant="caption" color="text.secondary">🎨 {u.activities}</Typography>}
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={1}>
            Posted by {u.posted_by_name}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

const NOTE_PRIORITY_CONFIG = {
  normal:  { label: 'Normal',  sx: { bgcolor: '#e0e0e0', color: '#555' } },
  high:    { label: 'High',    sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  urgent:  { label: 'Urgent',  sx: { bgcolor: '#ffebee', color: '#c62828' } }
};

const NOTE_STATUS_CONFIG = {
  pending:      { label: 'Pending',      sx: { bgcolor: '#fff3e0', color: '#e65100' } },
  acknowledged: { label: 'Acknowledged', sx: { bgcolor: '#e3f2fd', color: '#1565c0' } },
  completed:    { label: 'Completed',    sx: { bgcolor: '#e8f5e9', color: '#2e7d32' } }
};

const todayStr = () => new Date().toISOString().slice(0, 10);

function CareNotesTab({ studentId }) {
  const EMPTY_FORM = { note_date: todayStr(), title: '', note_text: '', priority: 'normal' };
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, note: null });
  const [editForm, setEditForm] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, note: null });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') => setSnack({ open: true, message, severity });

  const fetchNotes = useCallback(async () => {
    try {
      const res = await parentAPI.careNotes.list({ student_id: studentId });
      setNotes(res.data.notes || []);
    } catch {
      showSnack('Failed to load care notes', 'error');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const validateForm = (f) => {
    const errs = {};
    if (!f.note_date) errs.note_date = 'Date is required';
    else if (f.note_date < todayStr()) errs.note_date = 'Date cannot be in the past';
    if (!f.note_text.trim()) errs.note_text = 'Note text is required';
    else if (f.note_text.length > 2000) errs.note_text = 'Maximum 2000 characters';
    if (f.title && f.title.length > 200) errs.title = 'Maximum 200 characters';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validateForm(form);
    setFormErrors(errs);
    if (Object.keys(errs).length) return;

    setSubmitting(true);
    try {
      await parentAPI.careNotes.create({
        student_id: studentId,
        note_date: form.note_date,
        title: form.title || undefined,
        note_text: form.note_text.trim(),
        priority: form.priority
      });
      setForm(EMPTY_FORM);
      setFormErrors({});
      showSnack('Note sent to teacher');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to send note', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditOpen = (note) => {
    setEditForm({ title: note.title || '', note_text: note.note_text, priority: note.priority });
    setEditDialog({ open: true, note });
  };

  const handleEditSave = async () => {
    if (!editForm.note_text?.trim()) return;
    try {
      await parentAPI.careNotes.update(editDialog.note.id, {
        title: editForm.title || undefined,
        note_text: editForm.note_text.trim(),
        priority: editForm.priority
      });
      setEditDialog({ open: false, note: null });
      showSnack('Note updated');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to update note', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      await parentAPI.careNotes.remove(deleteDialog.note.id);
      setDeleteDialog({ open: false, note: null });
      showSnack('Note deleted');
      fetchNotes();
    } catch (err) {
      showSnack(err.response?.data?.message || 'Failed to delete note', 'error');
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  const sortedNotes = [...notes].sort((a, b) => (b.note_date || '').localeCompare(a.note_date || '') || (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteAddIcon color="primary" /> Add a Care Note for Today
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date"
              type="date"
              value={form.note_date}
              onChange={e => setForm(f => ({ ...f, note_date: e.target.value }))}
              inputProps={{ min: todayStr() }}
              error={!!formErrors.note_date}
              helperText={formErrors.note_date}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title (optional)"
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              error={!!formErrors.title}
              helperText={formErrors.title || `${form.title.length}/200`}
              inputProps={{ maxLength: 200 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note *"
              placeholder="E.g., Please give the blue inhaler if she has trouble breathing during PE..."
              value={form.note_text}
              onChange={e => setForm(f => ({ ...f, note_text: e.target.value }))}
              error={!!formErrors.note_text}
              helperText={formErrors.note_text || `${form.note_text.length}/2000`}
              inputProps={{ maxLength: 2000 }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ fontSize: '0.875rem', mb: 0.5 }}>Priority</FormLabel>
              <RadioGroup row value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
                <FormControlLabel value="high" control={<Radio size="small" color="warning" />}
                  label={<Typography color="warning.main" fontWeight={500}>High</Typography>} />
                <FormControlLabel value="urgent" control={<Radio size="small" color="error" />}
                  label={<Typography color="error.main" fontWeight={600}>Urgent</Typography>} />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ minWidth: 180 }}
            >
              {submitting ? 'Sending...' : 'Submit Care Note'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {sortedNotes.length === 0 ? (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 3, bgcolor: 'rgba(0,0,0,0.02)' }}>
          <NoteAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No care notes yet. Use the form above to send the first one.</Typography>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {sortedNotes.map(note => (
            <Card key={note.id} elevation={1} sx={{ borderRadius: 2, borderLeft: `4px solid ${note.priority === 'urgent' ? '#c62828' : note.priority === 'high' ? '#e65100' : '#9e9e9e'}` }}>
              <CardContent sx={{ pb: '12px !important' }}>
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(note.note_date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </Typography>
                      <Chip size="small" label={NOTE_PRIORITY_CONFIG[note.priority]?.label || note.priority} sx={NOTE_PRIORITY_CONFIG[note.priority]?.sx} />
                      <Chip size="small" label={NOTE_STATUS_CONFIG[note.status]?.label || note.status} sx={NOTE_STATUS_CONFIG[note.status]?.sx} />
                    </Box>
                    {note.title && (
                      <Typography variant="subtitle2" fontWeight={600}>{note.title}</Typography>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {note.note_text}
                    </Typography>
                    {note.status === 'completed' && note.completion_remark && (
                      <Box sx={{ mt: 1, p: 1, bgcolor: '#e8f5e9', borderRadius: 1 }}>
                        <Typography variant="caption" color="success.dark" fontWeight={600}>Teacher's remark: </Typography>
                        <Typography variant="caption" color="success.dark">{note.completion_remark}</Typography>
                      </Box>
                    )}
                  </Box>
                  {note.status === 'pending' && (
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEditOpen(note)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, note })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <MobileFormDialog
        open={editDialog.open}
        onClose={() => setEditDialog({ open: false, note: null })}
        title="Edit Care Note"
        actions={
          <>
            <Button onClick={() => setEditDialog({ open: false, note: null })}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave} disabled={!editForm.note_text?.trim()}>Save</Button>
          </>
        }
      >
        <TextField fullWidth label="Title (optional)" value={editForm.title || ''} sx={{ mt: 1, mb: 2 }}
          onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} inputProps={{ maxLength: 200 }} />
        <TextField fullWidth multiline rows={4} label="Note *" value={editForm.note_text || ''}
          onChange={e => setEditForm(f => ({ ...f, note_text: e.target.value }))}
          helperText={`${(editForm.note_text || '').length}/2000`} inputProps={{ maxLength: 2000 }} />
        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <FormLabel component="legend">Priority</FormLabel>
          <RadioGroup row value={editForm.priority || 'normal'} onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}>
            <FormControlLabel value="normal" control={<Radio size="small" />} label="Normal" />
            <FormControlLabel value="high" control={<Radio size="small" color="warning" />} label="High" />
            <FormControlLabel value="urgent" control={<Radio size="small" color="error" />} label="Urgent" />
          </RadioGroup>
        </FormControl>
      </MobileFormDialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, note: null })} fullWidth maxWidth="sm">
        <DialogTitle>Delete Care Note?</DialogTitle>
        <DialogContent>
          <Typography>This note will be permanently deleted. Are you sure?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, note: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
}

const TabTile = ({ tab, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      cursor: 'pointer',
      userSelect: 'none',
      borderRadius: 999,
      px: 2.5,
      py: 1,
      fontWeight: 600,
      color: active ? '#fff' : tab.color,
      bgcolor: active ? tab.color : alpha(tab.color, 0.1),
      transition: 'all 0.2s',
      '&:hover': { bgcolor: active ? tab.color : alpha(tab.color, 0.18) },
      '& .MuiSvgIcon-root': { fontSize: 20 },
    }}
  >
    {tab.icon}
    <Typography variant="body2" fontWeight={600} sx={{ color: 'inherit' }}>{tab.label}</Typography>
  </Box>
);

const ChildDetails = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user, logout, isFeatureEnabled } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeKey, setActiveKey] = useState(searchParams.get('tab') === 'daycare' ? 'Daycare' : 'Attendance');
  const [childInfo, setChildInfo] = useState(null);

  const generalTabs = [
    { label: 'Attendance', icon: <SchoolIcon />, content: <AttendanceTab studentId={studentId} />, color: '#1976d2' },
    { label: 'Marks', icon: <MarksIcon />, content: <MarksTab studentId={studentId} />, featureKey: 'academics', color: '#f59e0b' },
    { label: 'Fees', icon: <FeeIcon />, content: <FeesTab studentId={studentId} />, color: '#16a34a' },
    { label: 'Daily Updates', icon: <UpdatesIcon />, content: <DailyUpdatesTab studentId={studentId} />, color: '#0284c7' },
  ].filter((t) => (!t.featureKey || isFeatureEnabled(t.featureKey)) && !!childInfo?.class);

  const daycareTabs = [
    { label: 'Daycare', icon: <DaycareIcon />, content: <DaycareTab studentId={studentId} />, color: '#7c3aed' },
    { label: 'Care Notes', icon: <CareNoteIcon />, content: <CareNotesTab studentId={studentId} />, color: '#0f766e' },
  ].filter(() => !!childInfo?.daycare_enrolled);

  const tabs = [...generalTabs, ...daycareTabs];
  const activeTab = tabs.find((t) => t.label === activeKey) || tabs[0];

  const fetchChildInfo = useCallback(async () => {
    try {
      const res = await parentAPI.getChildren();
      const child = res.data.children.find(c => c.id === studentId);
      setChildInfo(child || null);
    } catch (err) {
      console.error('Failed to load child info:', err);
    }
  }, [studentId]);

  useEffect(() => { fetchChildInfo(); }, [fetchChildInfo]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/parent/dashboard')}
          sx={{ mb: 3 }}
        >
          Back to Dashboard
        </Button>

        {childInfo && (
          <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
                {childInfo.name?.[0]?.toUpperCase() || 'S'}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700}>{childInfo.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {childInfo.admission_number}
                  {childInfo.class?.name && <> &bull; {childInfo.class.name}</>}
                </Typography>
                {childInfo.gender && (
                  <Chip label={childInfo.gender} size="small" sx={{ mt: 0.5 }} />
                )}
              </Box>
            </Box>
          </Paper>
        )}

        <Paper elevation={1}>
        <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          {generalTabs.length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
                General
              </Typography>
              <Box display="flex" gap={1.25} flexWrap="wrap" mt={1} mb={2}>
                {generalTabs.map((t) => (
                  <TabTile key={t.label} tab={t} active={activeKey === t.label} onClick={() => setActiveKey(t.label)} />
                ))}
              </Box>
            </>
          )}

          {daycareTabs.length > 0 && (
            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 2,
                bgcolor: alpha('#7c3aed', 0.06),
                border: '1px dashed',
                borderColor: alpha('#7c3aed', 0.35),
              }}
            >
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1, color: '#7c3aed' }}>
                Daycare
              </Typography>
              <Box display="flex" gap={1.25} flexWrap="wrap" mt={1}>
                {daycareTabs.map((t) => (
                  <TabTile key={t.label} tab={t} active={activeKey === t.label} onClick={() => setActiveKey(t.label)} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
          <Box p={3}>
            {activeTab?.content}
          </Box>
        </Paper>
      </Container>
  );
};

export default ChildDetails;
